import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { RequestedSupportingItem } from '@/features/jobs/details/tax-type/info-gathering/RequestedSupportingItem'
import type { PersonalInfoSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/personal-info/PersonalInfoSupportingItemInput'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { InfoGatheringStore } from '@/features/jobs/details/tax-type/info-gathering/InfoGatheringStore'
import type {
  FetchPersonalProfileVersionForJobFn,
  PersonalProfileEditorViewModelFactory,
} from '@/features/tax-profile'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import type { PersonalProfileVersion } from '@/features/tax-profile/individual-profiles/PersonalProfile'
import { ignoreAbortError } from '@/utils/abort-util'
import { formatFullName } from '@/utils/full-name-util'
import { debounceTime, filter, Subject, switchMap } from 'rxjs'
import { withAbortSignal } from '@/utils/rx-util'
import type { SnackbarRetryWrapperFactory } from '@/features/shell'

/**
 * View model for a personal info supporting item.
 */
export class PersonalInfoRequestedSupportingItemViewModel
  implements ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * @see {next$}
   * @private
   */
  private readonly _nextSubject = new Subject<unknown>()

  /**
   * Emits when we intend to navigate to the next item.
   */
  readonly next$ = this._nextSubject.asObservable()

  /**
   * Editor VM for the personal profile.
   */
  readonly personalProfileEditorVm

  /**
   * The supporting item input type.
   */
  readonly type = 'PersonalInfo' as const

  /**
   * The personal profile version associated with this item, if any.
   */
  @observable accessor profile: PersonalProfileVersion | null = null

  constructor(
    private readonly _job: SubmittedJob,
    public readonly item: RequestedSupportingItem<PersonalInfoSupportingItemInput>,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    createPersonalProfileEditorViewModel: PersonalProfileEditorViewModelFactory,
    private _fetchPersonalProfileVersionForJob: FetchPersonalProfileVersionForJobFn,
  ) {
    this.personalProfileEditorVm = createPersonalProfileEditorViewModel({
      isReadOnly: () => this.isReadOnly,
      onSaved: (profile) =>
        this._infoGatheringStore
          .providePersonalProfile(this._job.id, this.item, profile)
          .then(() => {
            this.setProfile(profile)
            this._nextSubject.next(undefined)
          }),
    })

    // Reload the profile when the provided profile changes.
    this._lifecycle.manage(
      this.item.input.providedChanges$.pipe(
        debounceTime(200),
        filter(
          (provided) =>
            !this.profile?.equalsIdentity(
              provided?.profileId,
              provided?.profileVersion,
            ),
        ),
        switchMap(() => withAbortSignal((signal) => this._fetchCore(signal))),
      ),
    )
  }

  /**
   * Whether the item is read-only.
   */
  @computed get isReadOnly() {
    return this._job.status === 'Closed'
  }

  /**
   * Whether the item is considered completed.
   */
  @computed get isCompleted() {
    return (
      this.item.isCompleted &&
      // Only treat as completed once we have the profile's full name.
      Boolean(this.fullName)
    )
  }

  /**
   * Gets the full name of the profile.
   */
  @computed get fullName() {
    if (!this.profile) {
      return ''
    }

    return formatFullName(
      this.profile.personalInfo.firstName,
      this.profile.personalInfo.middleInitial,
      this.profile.personalInfo.lastName,
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(ignoreAbortError)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Sets the profile.
   */
  @action.bound
  setProfile(profile: PersonalProfileVersion | null) {
    this.profile = profile
    this.personalProfileEditorVm.reset(profile)
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  showDialog() {
    this.personalProfileEditorVm.showDialog(this.profile)
  }

  /**
   * Fetches the profile if one has been provided.
   */
  fetch = task.resolved(async (signal: AbortSignal) => {
    await this._fetchCore(signal)
  })

  /**
   * Core fetch logic that doesn't reset task state.
   * @private
   */
  private async _fetchCore(signal: AbortSignal) {
    const withRetry = this._createSnackbarRetryWrapper(
      'Unable to retrieve personal profile information',
      signal,
    )
    if (!this.item.input.provided) {
      // The underlying item has no provided value, no need to fetch it, but we
      // should fetch the fallback, so it can be pre-filled.
      this.setProfile(null)
      await withRetry(() => this.personalProfileEditorVm.fetchFallback(signal))
      return
    }

    const profile = await withRetry(() =>
      this._fetchPersonalProfileVersionForJob(this._job.id, signal),
    )
    signal.throwIfAborted()

    this.setProfile(profile)
  }
}
